import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select as ChakraSelect,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';
import { Buildings, CalendarBlank, CaretDown } from 'phosphor-react';
import { useAuditoriaInteligente } from '../../../../hooks/useAuditoriaInteligente';
import { ICompany } from '../../../../hooks/useCompanies';
import { IData } from '../../../../types/shared';
import { IAuditoriaEmpresa } from '../types';
import { UI } from '../ui';

export interface IPeriodo {
  ini: string; // 'YYYY-MM'
  fim: string; // 'YYYY-MM'
}

interface EmpresaOption {
  value: number;
  label: string;
  empresa: IAuditoriaEmpresa;
}

interface ContextBarProps {
  empresa: IAuditoriaEmpresa | null;
  onSelectEmpresa: (empresa: IAuditoriaEmpresa) => void;
  /** Limpa a empresa selecionada (remove o filtro). */
  onClearEmpresa: () => void;
  periodo: IPeriodo;
  onPeriodo: (periodo: IPeriodo) => void;
  /** Esconde os dados da empresa (telas multi-empresa). */
  multi?: boolean;
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const ANO_ATUAL = 2026;
const ANOS = [ANO_ATUAL - 3, ANO_ATUAL - 2, ANO_ATUAL - 1, ANO_ATUAL];

/** Abrevia o ano para 2 dígitos (2026 → 26). */
const yy = (y: number): string => String(y).slice(2);

interface IPreset {
  label: string;
  range: IPeriodo;
}

const PRESETS: IPreset[] = [
  { label: '2026', range: { ini: '2026-01', fim: '2026-12' } },
  { label: '2025', range: { ini: '2025-01', fim: '2025-12' } },
  { label: '1º sem/26', range: { ini: '2026-01', fim: '2026-06' } },
  { label: 'Últ. 12m', range: { ini: '2025-07', fim: '2026-06' } },
  { label: '24→26', range: { ini: '2024-01', fim: '2026-12' } },
];

const toAbs = (ym: string): number => {
  const [y, m] = ym.split('-').map(Number);
  return y * 12 + (m - 1);
};

type BuscaModo = 'tudo' | 'razao' | 'cnpj' | 'id';

const MODOS: [BuscaModo, string][] = [
  ['tudo', 'Tudo'],
  ['razao', 'Razão'],
  ['cnpj', 'CNPJ'],
  ['id', 'Id'],
];

const PLACEHOLDER: Record<BuscaModo, string> = {
  tudo: 'Buscar por ID, CNPJ ou Razão Social…',
  razao: 'Digite a razão social…',
  cnpj: 'Digite o CNPJ…',
  id: 'Digite o ID (external_id)…',
};

const MODO_LABEL: Record<BuscaModo, string> = {
  tudo: 'Tudo',
  razao: 'Razão',
  cnpj: 'CNPJ',
  id: 'ID',
};

const MODO_DESC: Record<BuscaModo, string> = {
  tudo: 'Tudo (ID · CNPJ · Razão)',
  razao: 'Razão social',
  cnpj: 'CNPJ',
  id: 'ID (external_id)',
};

/** Chip compacto "rótulo + valor" da ficha da empresa (não quebra; trunca o valor). */
function Chip({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Flex
      align="baseline"
      gap={1}
      px={2}
      py={1}
      borderRadius="7px"
      bg={UI.neutralSoft}
      whiteSpace="nowrap"
      flexShrink={0}
      maxW="230px"
    >
      <Text
        fontSize="9.5px"
        fontWeight={700}
        letterSpacing=".4px"
        color={UI.faint}
        textTransform="uppercase"
      >
        {label}
      </Text>
      <Text fontSize="12px" fontWeight={700} color={accent ? UI.brand : UI.ink} isTruncated>
        {value}
      </Text>
    </Flex>
  );
}

const toEmpresa = (c: ICompany): IAuditoriaEmpresa => ({
  id: c.id,
  external_id: c.external_id,
  nome: c.nome || c.razao_social,
  razao_social: c.razao_social,
  cnpj: c.cnpj,
  uf: c.uf,
  cidade: c.cidade,
  regime: c.regime_tributario_atualizado,
});

export function ContextBar({
  empresa,
  onSelectEmpresa,
  onClearEmpresa,
  periodo,
  onPeriodo,
  multi,
}: ContextBarProps) {
  const { getEmpresas } = useAuditoriaInteligente();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [modo, setModo] = useState<BuscaModo>('tudo');
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // monta os filtros conforme o campo escolhido no toggle (Tudo/Razão/CNPJ/Id)
  const filtros = useMemo(() => {
    const base = { page: 1, size: 30 };
    if (modo === 'razao') return { ...base, razao_social: debounced };
    if (modo === 'cnpj') return { ...base, cnpj: debounced };
    if (modo === 'id') return { ...base, external_id: debounced };
    return { ...base, search: debounced };
  }, [modo, debounced]);

  const onInputChange = (value: string) => {
    setSearch(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(value), 400);
  };

  const { data, isFetching } = useQuery<IData<ICompany[]>>({
    queryKey: ['ai-empresas-select', filtros],
    queryFn: getEmpresas,
    enabled: menuOpen,
  });

  const options: EmpresaOption[] = useMemo(
    () =>
      (data?.data?.items ?? []).map(c => ({
        value: c.id,
        label: c.razao_social || c.nome,
        empresa: toEmpresa(c),
      })),
    [data],
  );

  const setIni = (m: number, y: number) => {
    const ini = `${y}-${String(m + 1).padStart(2, '0')}`;
    const next = { ...periodo, ini };
    if (toAbs(ini) > toAbs(periodo.fim)) next.fim = ini;
    onPeriodo(next);
  };

  const setFim = (m: number, y: number) => {
    const fim = `${y}-${String(m + 1).padStart(2, '0')}`;
    const next = { ...periodo, fim };
    if (toAbs(fim) < toAbs(periodo.ini)) next.ini = fim;
    onPeriodo(next);
  };

  const [iniY, iniM] = periodo.ini.split('-').map(Number);
  const [fimY, fimM] = periodo.fim.split('-').map(Number);

  return (
    <Flex
      align="center"
      gap={{ base: 3, md: 4 }}
      rowGap={2}
      wrap={{ base: 'wrap', lg: 'nowrap' }}
      bg="white"
      borderBottom="1px solid"
      borderColor={UI.border}
      px={{ base: 4, md: 8 }}
      py={2.5}
      position="sticky"
      top={0}
      zIndex={25}
      boxShadow="0 1px 0 rgba(15,23,42,0.04)"
    >
      {/* ZONA A — busca de empresa (toggle de campo + select na MESMA linha) */}
      <Flex
        align="center"
        gap={2}
        flex={{ base: '1 1 100%', md: '0 1 480px' }}
        minW={{ base: '100%', md: '340px' }}
        flexShrink={0}
      >
        <Menu placement="bottom-start" autoSelect={false}>
          <MenuButton
            display="inline-flex"
            alignItems="center"
            gap="5px"
            h="38px"
            px="11px"
            flexShrink={0}
            borderRadius="9px 0 0 9px"
            border="1px solid"
            borderColor={UI.border}
            borderRight="none"
            bg={UI.neutralSoft}
            fontSize="12px"
            fontWeight={700}
            color={UI.muted}
            whiteSpace="nowrap"
            _hover={{ bg: '#E6EBF0', color: UI.ink }}
            _active={{ bg: '#E6EBF0' }}
          >
            {MODO_LABEL[modo]}
            <CaretDown size={11} weight="bold" />
          </MenuButton>
          <MenuList minW="180px" py={1} boxShadow="0 8px 24px rgba(20,40,60,.14)">
            {MODOS.map(([m]) => (
              <MenuItem
                key={m}
                onClick={() => setModo(m)}
                fontSize="13px"
                fontWeight={modo === m ? 700 : 500}
                color={modo === m ? UI.brand : UI.ink}
                bg={modo === m ? UI.infoSoft : undefined}
              >
                {MODO_DESC[m]}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        <Box flex={1} minW={0}>
          <Select<EmpresaOption>
            placeholder={PLACEHOLDER[modo]}
            options={options}
            value={
              empresa
                ? { value: empresa.id, label: empresa.razao_social || empresa.nome, empresa }
                : null
            }
            inputValue={search}
            onInputChange={onInputChange}
            onMenuOpen={() => setMenuOpen(true)}
            onMenuClose={() => setMenuOpen(false)}
            isClearable
            onChange={opt => (opt ? onSelectEmpresa(opt.empresa) : onClearEmpresa())}
            isLoading={isFetching}
            filterOption={null}
            noOptionsMessage={() =>
              debounced ? 'Nenhuma empresa encontrada' : 'Digite para buscar'
            }
            loadingMessage={() => 'Buscando…'}
            size="sm"
            formatOptionLabel={opt => (
              <Flex align="center" gap={2.5}>
                <Flex
                  align="center"
                  justify="center"
                  w="30px"
                  h="30px"
                  borderRadius="8px"
                  bg={UI.infoSoft}
                  color={UI.brand}
                  fontWeight={800}
                  fontSize="11px"
                  flexShrink={0}
                >
                  <Buildings size={15} />
                </Flex>
                <Box minW={0}>
                  <Text fontWeight={700} fontSize="13px" noOfLines={1}>
                    {opt.empresa.razao_social || opt.empresa.nome}
                  </Text>
                  <Text fontSize="11px" color={UI.muted} noOfLines={1}>
                    ID {opt.empresa.external_id} · {opt.empresa.cnpj}
                  </Text>
                </Box>
              </Flex>
            )}
            chakraStyles={{
              container: provided => ({ ...provided, width: '100%' }),
              control: provided => ({
                ...provided,
                borderRadius: '0 9px 9px 0',
                minHeight: '38px',
              }),
            }}
          />
        </Box>
      </Flex>

      {/* ZONA B — ficha (só os dados; o nome já aparece no select, sem duplicar) */}
      {!multi && empresa && (
        <HStack
          spacing={1.5}
          minW={0}
          flex={{ base: '1 1 100%', lg: '0 1 auto' }}
          order={{ base: 3, lg: 0 }}
          overflow="hidden"
        >
          <Chip label="ID" value={String(empresa.external_id)} accent />
          <Chip label="CNPJ" value={empresa.cnpj} />
          {empresa.regime && (
            <Badge
              bg={UI.infoSoft}
              color={UI.brand}
              borderRadius="full"
              px={2.5}
              py={1}
              fontSize="11px"
              textTransform="none"
              fontWeight={700}
              flexShrink={0}
              maxW="220px"
              isTruncated
              title={empresa.regime}
            >
              {empresa.regime.split(' - ')[0]}
            </Badge>
          )}
          {empresa.uf && (
            <Box
              px={2}
              py={1}
              borderRadius="full"
              bg={UI.neutralSoft}
              color={UI.muted}
              fontSize="11px"
              fontWeight={700}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {empresa.uf}
            </Box>
          )}
        </HStack>
      )}

      {/* ZONA C — período global (sempre à direita, nunca encolhe) */}
      <Box ml="auto" flexShrink={0} order={{ base: 2, lg: 0 }}>
        <Popover placement="bottom-end" isLazy>
          <PopoverTrigger>
            <Button
              variant="unstyled"
              display="inline-flex"
              alignItems="center"
              gap={2}
              h="auto"
              px="13px"
              py="7px"
              borderRadius="9px"
              bg="white"
              border="1px solid"
              borderColor={UI.border}
              fontSize="13px"
              fontWeight={700}
              color={UI.ink}
              _hover={{ borderColor: '#CFE3F5' }}
            >
              <CalendarBlank size={16} color={UI.brand} />
              <Box as="span">
                {MESES[iniM - 1]}/{yy(iniY)} – {MESES[fimM - 1]}/{yy(fimY)}
              </Box>
              <CaretDown size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent w="340px" boxShadow="0 8px 30px rgba(20,40,60,.12)">
            <PopoverBody p={4}>
              <Text
                fontSize="10.5px"
                fontWeight={700}
                letterSpacing="1.4px"
                color={UI.muted}
                textTransform="uppercase"
                mb={3}
              >
                Período — De / Até
              </Text>

              <Flex direction="column" gap={3} mb={3.5}>
                <Box>
                  <Text
                    fontSize="10.5px"
                    fontWeight={700}
                    letterSpacing=".6px"
                    color={UI.muted}
                    textTransform="uppercase"
                    mb={1.5}
                  >
                    De
                  </Text>
                  <Flex gap={2}>
                    <ChakraSelect
                      size="sm"
                      flex={1}
                      value={iniM - 1}
                      onChange={e => setIni(Number(e.target.value), iniY)}
                    >
                      {MESES.map((m, i) => (
                        <option key={m} value={i}>
                          {m}
                        </option>
                      ))}
                    </ChakraSelect>
                    <ChakraSelect
                      size="sm"
                      w="92px"
                      value={iniY}
                      onChange={e => setIni(iniM - 1, Number(e.target.value))}
                    >
                      {ANOS.map(y => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </ChakraSelect>
                  </Flex>
                </Box>

                <Box>
                  <Text
                    fontSize="10.5px"
                    fontWeight={700}
                    letterSpacing=".6px"
                    color={UI.muted}
                    textTransform="uppercase"
                    mb={1.5}
                  >
                    Até
                  </Text>
                  <Flex gap={2}>
                    <ChakraSelect
                      size="sm"
                      flex={1}
                      value={fimM - 1}
                      onChange={e => setFim(Number(e.target.value), fimY)}
                    >
                      {MESES.map((m, i) => (
                        <option key={m} value={i}>
                          {m}
                        </option>
                      ))}
                    </ChakraSelect>
                    <ChakraSelect
                      size="sm"
                      w="92px"
                      value={fimY}
                      onChange={e => setFim(fimM - 1, Number(e.target.value))}
                    >
                      {ANOS.map(y => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </ChakraSelect>
                  </Flex>
                </Box>
              </Flex>

              <Text
                fontSize="10.5px"
                fontWeight={700}
                letterSpacing="1.4px"
                color={UI.muted}
                textTransform="uppercase"
                mb={2}
              >
                Atalhos
              </Text>
              <Wrap spacing={1.5}>
                {PRESETS.map(p => (
                  <WrapItem key={p.label}>
                    <Button
                      size="xs"
                      variant="outline"
                      borderColor={UI.border}
                      color={UI.muted}
                      fontWeight={700}
                      _hover={{ bg: UI.rowHover, color: UI.brand, borderColor: '#CFE3F5' }}
                      onClick={() => onPeriodo(p.range)}
                    >
                      {p.label}
                    </Button>
                  </WrapItem>
                ))}
              </Wrap>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    </Flex>
  );
}
